import type { Json } from "@base-hormonal/database";

export const PROFILE_MODULES = [
  { id: "assistente", label: "Assistente", legacyTab: "chats" },
  { id: "protocolos", label: "Protocolos", legacyTab: "protocols" },
  { id: "exames", label: "Exames", legacyTab: "exams" },
  { id: "nutricao", label: "Nutrição", legacyTab: "nutrition" },
  { id: "treino", label: "Treino", legacyTab: "training" },
  { id: "corpo", label: "Corpo", legacyTab: "body" },
  { id: "evolucao", label: "Evolução", legacyTab: "progress" },
] as const;

export type ProfileModuleId = (typeof PROFILE_MODULES)[number]["id"];
export type UsageMode = "personal" | "professional";
export type ProfessionalPreset = "coach" | "nutritionist" | "trainer" | "other";
export type Capability =
  "multiple_profiles" | "report_branding" | "ai_drafts" | "report_pdf" | "private_files";

const PRESETS: Readonly<Record<ProfessionalPreset | "personal", readonly ProfileModuleId[]>> = {
  personal: ["assistente", "protocolos", "exames", "corpo", "evolucao"],
  coach: ["assistente", "protocolos", "exames", "corpo", "evolucao"],
  nutritionist: ["assistente", "exames", "nutricao", "corpo", "evolucao"],
  trainer: ["assistente", "treino", "corpo", "evolucao"],
  other: ["assistente", "protocolos", "exames"],
};

const MODE_CAPABILITIES: Readonly<Record<UsageMode, Readonly<Record<Capability, boolean>>>> = {
  personal: {
    multiple_profiles: false,
    report_branding: false,
    ai_drafts: true,
    report_pdf: true,
    private_files: true,
  },
  professional: {
    multiple_profiles: true,
    report_branding: true,
    ai_drafts: true,
    report_pdf: true,
    private_files: true,
  },
};

export const TECHNICAL_LIMITS = Object.freeze({
  artifact_source_chars: 100_000,
  exam_asset_bytes: 4 * 1024 * 1024,
  body_asset_bytes: 8 * 1024 * 1024,
  branding_asset_bytes: 2 * 1024 * 1024,
  report_pdf_bytes: 20 * 1024 * 1024,
  exam_signed_url_seconds: 60,
  body_signed_url_seconds: 300,
});

export interface PlatformAccess {
  usageMode: UsageMode;
  professionalProfile: ProfessionalPreset | null;
  capabilities: Readonly<Record<Capability, boolean>>;
  limits: Readonly<Record<string, number | null>>;
  source: string;
}

function objectJson(value: Json): Record<string, Json | undefined> {
  return value !== null && !Array.isArray(value) && typeof value === "object" ? value : {};
}

export function presetFor(
  usageMode: UsageMode,
  professionalProfile: ProfessionalPreset | null,
): ProfileModuleId[] {
  const key = usageMode === "personal" ? "personal" : (professionalProfile ?? "other");
  return [...PRESETS[key]];
}

export function normalizeAccess(
  usageMode: UsageMode,
  professionalProfile: ProfessionalPreset | null,
  entitlement: { capabilities: Json; limits: Json; source: string } | null,
): PlatformAccess {
  const capabilityValues = objectJson(entitlement?.capabilities ?? null);
  const capabilities = { ...MODE_CAPABILITIES[usageMode] };
  for (const key of Object.keys(capabilities) as Capability[]) {
    if (typeof capabilityValues[key] === "boolean") capabilities[key] = capabilityValues[key];
  }
  const limitValues = objectJson(entitlement?.limits ?? null);
  const limits: Record<string, number | null> = {
    max_profiles: null,
    monthly_ai_tokens: null,
    storage_bytes: null,
    ...TECHNICAL_LIMITS,
  };
  for (const [key, value] of Object.entries(limitValues)) {
    if (value === null || (typeof value === "number" && Number.isFinite(value)))
      limits[key] = value;
  }
  return Object.freeze({
    usageMode,
    professionalProfile: usageMode === "professional" ? professionalProfile : null,
    capabilities: Object.freeze(capabilities),
    limits: Object.freeze(limits),
    source: entitlement?.source ?? "mode_default",
  });
}

export function can(access: PlatformAccess, capability: Capability): boolean {
  return access.capabilities[capability];
}

export function profileLimitReached(access: PlatformAccess, clientCount: number): boolean {
  const maximum = access.limits.max_profiles;
  return typeof maximum === "number" && clientCount >= maximum;
}
